import { initializeApp, FirebaseApp } from 'firebase/app';
import {
    getRemoteConfig,
    fetchAndActivate,
    getValue,
    RemoteConfig,
    setCustomSignals
} from 'firebase/remote-config';
import { Analytics, getAnalytics, logEvent } from 'firebase/analytics';
import * as packageData from '../../package.json';
import { FirebaseEventParam } from '../models/firebaseEventParam.module';
import { GdprService } from '../common-classes/gdpr.service';
import { randomUUID } from 'crypto';
import { geoLocationService } from './geo-location.service';
import { store, type RootState } from '@/hotdesk-ui/store/store';

export type firebaseReturnDateType =
    | string
    | boolean
    | number
    | object
    | undefined;
export type firebaseDataType = 'string' | 'boolean' | 'number' | 'object';

const firebaseConfig = {
    apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
    authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
    storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
    measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,
    databaseURL: process.env.NEXT_PUBLIC_FIREBASE_DATEBASE
};

let app: FirebaseApp | undefined;
let remoteConfig: RemoteConfig | undefined;
let analytics: Analytics | undefined;
let signalInitialized = false;
let initPromise: Promise<void> | null = null;

if (typeof window !== 'undefined' && !app) {
    app = initializeApp(firebaseConfig);
    remoteConfig = getRemoteConfig(app);
    const isProduction =
        process.env.NEXT_PUBLIC_SELF_URL === 'https://www.hotdesk.com/';
    remoteConfig.settings.minimumFetchIntervalMillis = isProduction
        ? 3600000
        : 10000;
    analytics = getAnalytics(app);
}

const configCache: Map<string, firebaseReturnDateType> = new Map();

export const initialFetchRemoteConfig = async (
    force = false
): Promise<void> => {
    if (initPromise && !force) return initPromise;

    if (force) {
        configCache.clear();
    }

    initPromise = (async () => {
        try {
            if (!remoteConfig) return;

            if (!signalInitialized) {
                await restoreItidaCustomSignal();
            }

            await fetchAndActivate(remoteConfig);
        } catch (error) {
            console.error('Debugger >> Error fetching remote config:', error);
            initPromise = null;
            throw error;
        }
    })();

    return initPromise;
};

export const fetchRemoteConfig = async (
    key: string,
    type: firebaseDataType,
    skipCache: boolean = false
): Promise<firebaseReturnDateType> => {
    await initialFetchRemoteConfig();

    // Return cached value only if skipCache is false
    if (!skipCache && configCache.has(key)) {
        return configCache.get(key);
    }

    try {
        if (!remoteConfig) return undefined;
        const remoteConfigValue = getValue(remoteConfig, key);
        let returnType: firebaseReturnDateType = remoteConfigValue.asString();
        if (type === 'number') {
            returnType = remoteConfigValue.asNumber();
        } else if (type === 'boolean') {
            returnType = remoteConfigValue.asBoolean();
        } else if (type === 'object') {
            const string = remoteConfigValue.asString();
            try {
                returnType = JSON.parse(string);
            } catch {
                returnType = remoteConfigValue.asString();
            }
        } else {
            returnType = remoteConfigValue.asString();
        }

        configCache.set(key, returnType);
        return returnType;
    } catch (error) {
        console.error('Debugger >> Error fetching remote config:', error);
        return configCache.get(key) ?? undefined;
    }
};

export const firebaseAnalytics = async (
    eventName: string,
    config?: FirebaseEventParam
) => {
    if (!!process.env.IS_E2E_MODE) {
        return;
    }
    if (!analytics) return;
    const { version } = packageData;
    const gdprService = new GdprService();
    const geoData = geoLocationService.getGeoLocationData();
    const eventConfigData: FirebaseEventParam = {
        ...(gdprService.isConcentGiven() && { uuid: randomUUID() }),
        screen_url: window.location.href,
        page_location: window.location.href,
        ...(gdprService.isConcentGiven() && {
            os: window.navigator.platform
        }),
        ...(geoData &&
            gdprService.isConcentGiven() && {
                location_pin: geoData.locationZip
            }),
        ...(geoData &&
            gdprService.isConcentGiven() && {
                city: geoData.locationZip
            }),
        ...(geoData &&
            gdprService.isConcentGiven() && {
                country: geoData.locationZip
            }),
        platform: 'web_landing',
        timestamp: Date.now().toString(),
        environment: process.env.STAGE === 'PROD' ? 'production' : 'staging',
        version,
        ...config
    };
    await logEvent(analytics, eventName, eventConfigData);
};

export const setItidaCustomSignalFromStore = async () => {
    if (typeof window === 'undefined' || !remoteConfig) return;
    const state: RootState = store.getState();
    const companyGroup = state?.auth?.user?.companyGroups;
    const companyGroupId = companyGroup?.map((item) => item.id).join(',') ?? '';

    if (!companyGroupId) return;
    localStorage.setItem('company_group_id', companyGroupId);
    await setCustomSignals(remoteConfig, {
        company_group_id: companyGroupId,
        api_environment: 'stage'
    });

    const prevInterval = remoteConfig.settings.minimumFetchIntervalMillis;
    remoteConfig.settings.minimumFetchIntervalMillis = 0;
    await initialFetchRemoteConfig(true);
    remoteConfig.settings.minimumFetchIntervalMillis = prevInterval;
    signalInitialized = true;
};

export const restoreItidaCustomSignal = async () => {
    if (!remoteConfig || signalInitialized) return;
    const cached = localStorage.getItem('company_group_id');

    if (cached) {
        await setCustomSignals(remoteConfig, {
            company_group_id: cached
        });
    }

    signalInitialized = true;
};

export const clearItidaCustomSignal = async () => {
    signalInitialized = false;
    localStorage.removeItem('company_group_id');
    if (typeof window === 'undefined' || !analytics || !remoteConfig) return;
    await setCustomSignals(remoteConfig, {
        company_group_id: '',
        api_environment: ''
    });

    // Bypass Firebase's fetch throttle so the cleared signal is actually sent to
    // the server. Without this, fetchAndActivate returns the stale ITIDA config
    // from IndexedDB if we're still within minimumFetchIntervalMillis.
    const prevInterval = remoteConfig.settings.minimumFetchIntervalMillis;
    remoteConfig.settings.minimumFetchIntervalMillis = 0;
    await initialFetchRemoteConfig(true);
    remoteConfig.settings.minimumFetchIntervalMillis = prevInterval;
};
